"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useEffect, useMemo, use, useState } from "react";
import { defaultDocsLocale, docsLocaleCookieName, docsLocaleLabels, docsLocales, getDocsBrowserLocale, normalizeDocsLocale } from "./i18n-shared";
import type { DocsLocale } from "./i18n-shared";
import type { ReactNode } from "react";
export type { DocsLocale } from "./i18n-shared";
const storageKey = "llmgateway.locale";
const enMessages = {
  "language.label": "Language",
  "status.operational": "All systems operational",
  "enterpriseCta.title": "Ready for production?",
  "enterpriseCta.description": "Ship to production with SSO, audit logs, spend controls, and guardrails your security team will approve.",
  "enterpriseCta.link": "Explore Enterprise",
  "feedback.prompt": "How is this guide?",
  "feedback.good": "Good",
  "feedback.bad": "Bad",
  "feedback.thanks": "Thank you for your feedback!",
  "feedback.submitAgain": "Submit Again",
  "feedback.placeholder": "Leave your feedback...",
  "feedback.submit": "Submit",
  "aiSearch.trigger": "Ask AI",
  "aiSearch.title": "Ask AI",
  "aiSearch.disclaimer": "AI can be inaccurate, please verify the answers.",
  "aiSearch.close": "Close",
  "aiSearch.retry": "Retry",
  "aiSearch.clearChat": "Clear Chat",
  "aiSearch.answering": "AI is answering...",
  "aiSearch.askPlaceholder": "Ask a question",
  "aiSearch.abort": "Abort Answer",
  "aiSearch.you": "you",
  "aiSearch.assistant": "llmgateway",
  "aiSearch.unknownRole": "unknown",
  "aiSearch.searchFailed": "Failed to search",
  "aiSearch.searching": "Searching...",
  "aiSearch.searchResults": "{count} search results",
  "aiSearch.empty": "Start a new chat below.",
  "aiSearch.requestFailed": "Request Failed: {name}",
  "pageActions.copyMarkdown": "Copy Markdown",
  "pageActions.open": "Open",
  "pageActions.openInChatGPT": "Open in ChatGPT",
  "pageActions.openInClaude": "Open in Claude",
  "pageActions.openInLLMGateway": "Open in doteb",
  "pageActions.askPrompt": "Read {url}, I want to ask questions about it.",
  "features.showMore": "Show {count} more features"
} as const;
type MessageKey = keyof typeof enMessages;
const zhCNMessages: Record<MessageKey, string> = {
  "language.label": "语言",
  "status.operational": "所有系统运行正常",
  "enterpriseCta.title": "准备好用于生产了吗？",
  "enterpriseCta.description": "通过 SSO、审计日志、支出控制和安全团队认可的护栏，把服务稳定推向生产。",
  "enterpriseCta.link": "了解企业版",
  "feedback.prompt": "这篇指南有帮助吗？",
  "feedback.good": "有帮助",
  "feedback.bad": "没帮助",
  "feedback.thanks": "感谢你的反馈！",
  "feedback.submitAgain": "重新提交",
  "feedback.placeholder": "写下你的反馈...",
  "feedback.submit": "提交",
  "aiSearch.trigger": "问 AI",
  "aiSearch.title": "问 AI",
  "aiSearch.disclaimer": "AI 可能不准确，请核对答案。",
  "aiSearch.close": "关闭",
  "aiSearch.retry": "重试",
  "aiSearch.clearChat": "清空对话",
  "aiSearch.answering": "AI 正在回答...",
  "aiSearch.askPlaceholder": "输入问题",
  "aiSearch.abort": "停止回答",
  "aiSearch.you": "你",
  "aiSearch.assistant": "llmgateway",
  "aiSearch.unknownRole": "未知",
  "aiSearch.searchFailed": "搜索失败",
  "aiSearch.searching": "搜索中...",
  "aiSearch.searchResults": "{count} 条搜索结果",
  "aiSearch.empty": "在下方开始新对话。",
  "aiSearch.requestFailed": "请求失败：{name}",
  "pageActions.copyMarkdown": "复制 Markdown",
  "pageActions.open": "打开",
  "pageActions.openInChatGPT": "在 ChatGPT 打开",
  "pageActions.openInClaude": "在 Claude 打开",
  "pageActions.openInLLMGateway": "在 doteb 打开",
  "pageActions.askPrompt": "阅读 {url}，我想围绕这篇文档提问。",
  "features.showMore": "再显示 {count} 个功能"
};
const docsMessages: Record<DocsLocale, Record<MessageKey, string>> = {
  en: enMessages,
  "zh-CN": zhCNMessages
};
type TranslationValues = Record<string, string | number | boolean>;
interface DocsI18nContextValue {
  locale: DocsLocale;
  locales: readonly DocsLocale[];
  localeLabels: Record<DocsLocale, string>;
  setLocale: (locale: DocsLocale) => void;
  t: (key: MessageKey, values?: TranslationValues) => string;
}
const DocsI18nContext = createContext<DocsI18nContextValue | null>(null);
export function DocsI18nProvider(t0) {
  const $ = _c(14);
  const {
    children,
    defaultLocale: t1
  } = t0;
  const defaultLocale = t1 === undefined ? defaultDocsLocale : t1;
  const [locale, setLocaleState] = useState(defaultLocale);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const storedLocale = readCookie(docsLocaleCookieName) ?? window.localStorage.getItem(storageKey);
      const nextLocale = storedLocale ?? getDocsBrowserLocale(typeof navigator === "undefined" ? [] : navigator.languages);
      setLocaleState(normalizeDocsLocale(nextLocale));
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[2] !== locale) {
    t4 = () => {
      document.documentElement.lang = locale;
      document.documentElement.dir = "ltr";
    };
    t5 = [locale];
    $[2] = locale;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = nextLocale_0 => {
      const normalized = normalizeDocsLocale(nextLocale_0);
      setLocaleState(normalized);
      window.localStorage.setItem(storageKey, normalized);
      document.cookie = `${docsLocaleCookieName}=${encodeURIComponent(normalized)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const setLocale = t6;
  let t7;
  if ($[6] !== locale) {
    t7 = (key, values) => interpolate(docsMessages[locale][key] ?? enMessages[key], values);
    $[6] = locale;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t = t7;
  let t8;
  if ($[8] !== locale || $[9] !== t) {
    t8 = {
      locale,
      locales: docsLocales,
      localeLabels: docsLocaleLabels,
      setLocale,
      t
    };
    $[8] = locale;
    $[9] = t;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const value = t8;
  let t9;
  if ($[11] !== children || $[12] !== value) {
    t9 = <DocsI18nContext value={value}>{children}</DocsI18nContext>;
    $[11] = children;
    $[12] = value;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
export function useDocsI18n() {
  const context = use(DocsI18nContext);
  if (!context) {
    throw new Error("useDocsI18n must be used within DocsI18nProvider.");
  }
  return context;
}
function readCookie(name: string) {
  const prefix = `${name}=`;
  const value = document.cookie.split(";").map(item => item.trim()).find(item => item.startsWith(prefix));
  return value ? decodeURIComponent(value.slice(prefix.length)) : undefined;
}
function interpolate(message: string, values?: TranslationValues) {
  if (!values) {
    return message;
  }
  return Object.entries(values).reduce((result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)), message);
}